import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { call, put, select, takeEvery } from 'redux-saga/effects'

import { getDatabase } from '../api'
import { Price } from '../schema'
import { DeepReadonly, log } from '../utils'
import { selectEfficiencyPrices } from './efficiencyPrices'

const PREFIX = 'itemPrices'

export interface ItemPriceChange {
  readonly saleTag: string
  readonly stateKey: string
  readonly itemLabel: string
  readonly oldValue: number | string
  readonly newValue: number
  readonly cityIndex?: number
}

type ItemPricesState = DeepReadonly<{
  applying: boolean
  // Ticket found during Aug 14 audit: this used to not exist at all, so
  // applyItemPriceChangesResult threw away the `ok` the saga already
  // computed, and ItemPrices.tsx cleared the admin's pending edits on
  // `applying` going false regardless of whether the write actually
  // succeeded. A failed save (offline admin, rejected Firebase write) looked
  // identical to a successful one, and the edits were gone with no error.
  lastApplyOk: boolean | null
}>

const initialState: ItemPricesState = {
  applying: false,
  lastApplyOk: null,
}

interface ApplyPayload {
  readonly changes: readonly ItemPriceChange[]
  readonly itemsEnvKey?: string
  readonly newItems?: readonly { label: string; value: string }[]
}

interface DeleteItemPayload {
  readonly saleTag: string
  readonly itemValue: string
}

interface ReorderItemsPayload {
  readonly saleTag: string
  readonly items: readonly { label: string; value: string }[]
}

const itemPricesSlice = createSlice({
  name: PREFIX,
  initialState,
  reducers: {
    applyItemPriceChanges(state, _action: PayloadAction<ApplyPayload>) {
      state.applying = true
    },
    applyItemPriceChangesResult(
      state,
      { payload: { ok } }: PayloadAction<{ ok: boolean }>,
    ) {
      state.applying = false
      state.lastApplyOk = ok
    },
    deleteItem(_state, _action: PayloadAction<DeleteItemPayload>) {},
    deleteItemResult(_state, _action: PayloadAction<{ ok: boolean }>) {},
    reorderItems(_state, _action: PayloadAction<ReorderItemsPayload>) {},
  },
})

export const {
  actions: {
    applyItemPriceChanges,
    applyItemPriceChangesResult,
    deleteItem,
    deleteItemResult,
    reorderItems,
  },
  reducer: itemPrices,
} = itemPricesSlice

//#region selectors
interface GlobalState {
  [PREFIX]: ItemPricesState
}

export const selectItemPricesApplying = ({
  itemPrices: { applying },
}: GlobalState): boolean => applying

export const selectItemPricesLastApplyOk = ({
  itemPrices: { lastApplyOk },
}: GlobalState): boolean | null => lastApplyOk
//#endregion selectors

//#region sagas
function* applyItemPriceChangesWatcher(
  action: PayloadAction<ApplyPayload>,
): Generator {
  const { changes, itemsEnvKey, newItems } = action.payload
  try {
    const db = getDatabase()

    const grouped = new Map<string, ItemPriceChange[]>()
    for (const change of changes) {
      const path =
        typeof change.cityIndex === 'number'
          ? `EfficiencyPrices/${change.saleTag}/${change.stateKey}/pricesByCity/${change.cityIndex}/list`
          : `EfficiencyPrices/${change.saleTag}/${change.stateKey}/list`
      if (!grouped.has(path)) grouped.set(path, [])
      grouped.get(path)!.push(change)
    }

    const allPrices: any = yield select(selectEfficiencyPrices)

    // Every group below can be skipped by the two `continue`s, which used to
    // still reach `ok: true` at the end - the screen then cleared the admin's
    // pending edits on a save that wrote nothing at all. Tracking whether any
    // write actually happened is what makes `ok` mean something.
    let wroteSomething = false
    let skippedGroups = 0

    for (const [path, groupChanges] of Array.from(grouped)) {
      const first = groupChanges[0]!
      const saleData = allPrices[first.saleTag]
      if (!saleData) {
        skippedGroups++
        continue
      }

      const stateData = saleData[first.stateKey]
      if (!stateData) {
        skippedGroups++
        continue
      }

      let currentList: Price[]
      if (typeof first.cityIndex === 'number' && stateData.pricesByCity) {
        currentList = [...(stateData.pricesByCity[first.cityIndex]?.list || [])]
      } else {
        currentList = [...(stateData.list || [])]
      }

      for (const change of groupChanges) {
        const idx = currentList.findIndex((p) => p.label === change.itemLabel)
        if (idx !== -1) {
          currentList[idx] = { label: change.itemLabel, value: change.newValue }
        } else {
          currentList.push({ label: change.itemLabel, value: change.newValue })
        }
      }

      yield call(() => db.ref(path).set(currentList))
      wroteSomething = true
    }

    if (itemsEnvKey && newItems && newItems.length > 0) {
      const envRef = db.ref(`Misc/env/${itemsEnvKey}`)
      const snapshot: any = yield call(() => envRef.once('value'))
      const currentItems: Array<{ label: string; value: string }> =
        snapshot.val() || []
      yield call(() => envRef.set([...currentItems, ...newItems]))
      wroteSomething = true
    }

    if (skippedGroups > 0) {
      log(
        `${applyItemPriceChangesWatcher.name}() skipped ${skippedGroups} group(s) - price data not loaded for them`,
      )
    }

    // Nothing asked for and nothing written is a no-op, not a failure. But
    // changes that were asked for and all got skipped is a failed save, and
    // the admin has to keep their edits.
    const askedForSomething = grouped.size > 0 || Boolean(newItems?.length)
    yield put(
      applyItemPriceChangesResult({
        ok: wroteSomething || !askedForSomething,
      }),
    )
  } catch (e) {
    log(`${applyItemPriceChangesWatcher.name}() error: ${e}`)
    yield put(applyItemPriceChangesResult({ ok: false }))
  }
}

function* deleteItemWatcher(
  action: PayloadAction<DeleteItemPayload>,
): Generator {
  const { saleTag, itemValue } = action.payload
  try {
    const db = getDatabase()

    const envRef = db.ref(`Misc/env/${saleTag}`)
    const envSnap: any = yield call(() => envRef.once('value'))
    const currentItems: Array<{ label: string; value: string }> =
      envSnap.val() || []
    yield call(() =>
      envRef.set(currentItems.filter((it) => it.value !== itemValue)),
    )

    const allPrices: any = yield select(selectEfficiencyPrices)
    const saleData = allPrices?.[saleTag]
    if (saleData) {
      for (const [stateKey, stateData] of Object.entries(saleData)) {
        const data = stateData as any
        if (data?.pricesByCity) {
          for (let i = 0; i < data.pricesByCity.length; i++) {
            const list: Price[] = data.pricesByCity[i]?.list || []
            if (list.some((p) => p.label === itemValue)) {
              const path = `EfficiencyPrices/${saleTag}/${stateKey}/pricesByCity/${i}/list`
              yield call(() =>
                db.ref(path).set(list.filter((p) => p.label !== itemValue)),
              )
            }
          }
        } else if (data?.list) {
          const list: Price[] = data.list
          if (list.some((p) => p.label === itemValue)) {
            const path = `EfficiencyPrices/${saleTag}/${stateKey}/list`
            yield call(() =>
              db.ref(path).set(list.filter((p) => p.label !== itemValue)),
            )
          }
        }
      }
    }

    yield put(deleteItemResult({ ok: true }))
  } catch (e) {
    log(`${deleteItemWatcher.name}() error: ${e}`)
    yield put(deleteItemResult({ ok: false }))
  }
}

function* reorderItemsWatcher(
  action: PayloadAction<ReorderItemsPayload>,
): Generator {
  const { saleTag, items } = action.payload
  try {
    const db = getDatabase()
    yield call(() => db.ref(`Misc/env/${saleTag}`).set([...items]))
  } catch (e) {
    log(`${reorderItemsWatcher.name}() error: ${e}`)
  }
}

export function* itemPricesSaga() {
  yield takeEvery(applyItemPriceChanges, applyItemPriceChangesWatcher)
  yield takeEvery(deleteItem, deleteItemWatcher)
  yield takeEvery(reorderItems, reorderItemsWatcher)
}
//#endregion sagas
